import { createContext, ReactNode, use, useCallback, useEffect, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import { CustomMusic, CustomMusicManifest, Hymn, SongSlug } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { recordRecentlyAddedSong, recordRecentlyDeletedSong, useCustomMusic } from '../data/use_custom_music.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { Button } from '../pages/onboarding/button.tsx';
import { Message, TextInput } from '../pages/onboarding/common.tsx';
import { HiddenSongCheckBox, useSongVisibility } from '../pages/onboarding/hidden_song_check_box.tsx';
import { alert, confirm } from './confirm.tsx';
import { ModalSpinner } from './modal_spinner.tsx';
import { getHymnFromSlug, isCustomSong } from './path.ts';
import { getPlatform, Platform } from './platform.ts';
import { Pencil } from './shared.tsx';
import { Spinner } from './spinner.tsx';
import { useHoldAppReload } from './use_hold_app_reload.ts';
import { holdAppReload } from './user_activity.ts';

const EditButtonContext = createContext<{ hasPermission: boolean } | undefined>(undefined);

export function EditButtonContextProvider({ children }: { children: ReactNode }) {
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  return <EditButtonContext value={{ hasPermission: isInternalUser || isOrgAdmin }}>{children}</EditButtonContext>;
}

enum UploadStatus {
  Before = 'before',
  Uploading = 'uploading',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

let busyUploading = false;

const EditCustomSong: ConfirmDialog<
  { customMusic: CustomMusic; customMusicManifest: CustomMusicManifest | undefined; songSlug: SongSlug },
  { songSlug?: SongSlug }
> = ({ customMusic, customMusicManifest, songSlug, proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);

  const songNameInputRef = useRef<HTMLInputElement>(null);
  const { currentOrg } = useCurrentOrg();
  const fileChooserInputRef = useRef<HTMLInputElement>(null);
  const [fileToUpload, setFileToUpload] = useState<File | undefined>();
  const psalmNumberInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploadStatus>(UploadStatus.Before);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const hymn = getHymnFromSlug({ songSlug, customMusic });
  const [songName, setSongName] = useState(() => hymn?.title);
  const songNameIsValid = !songName?.includes('/');
  let hymnPsalmNumber: number | undefined;
  {
    const { number } = hymn?.psalm?.match(/(?<number>\d+)/)?.groups ?? {};
    hymnPsalmNumber = number ? Number(number) : undefined;
  }
  const [psalmNumber, setPsalmNumber] = useState<number | undefined>(() => hymnPsalmNumber);
  const [showSpinner, setShowSpinner] = useState(false);
  const isValidPsalmNumber = psalmNumber === undefined || (psalmNumber >= 1 && psalmNumber <= 150);
  const [isHiddenSong, setIsHiddenSong] = useState(() => !!currentOrg?.hiddenSongs?.includes(songSlug));
  const { setSongVisibility } = useSongVisibility();

  // A reload would take this dialog's unsaved name and psalm number away, and would abort an upload
  // mid-flight (or skip the manifest bookkeeping that follows it), so hold the app reload off for as
  // long as either is true - the upload outlives the dialog when the user cancels (issue #3265).
  // the dialog holds the reload while it is open; the request below holds it while in flight,
  // because cancelling hides the dialog (and unmounts this) while the upload keeps going
  useHoldAppReload(show);

  useEffect(() => {
    songNameInputRef.current?.focus();
  }, [songNameInputRef, setPsalmNumber, songName]);

  const onSongNameChanged = useCallback(() => {
    setSongName(songNameInputRef.current?.value ?? '');
  }, [songNameInputRef, setSongName]);

  const setHiddenSongWrapper = useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (value) => {
      const newValue = typeof value === 'function' ? value(isHiddenSong) : value;
      setIsHiddenSong(newValue);
      void setSongVisibility({ songSlug, isVisible: !newValue });
    },
    [isHiddenSong, setSongVisibility, songSlug],
  );

  const onPsalmNumberChanged = useCallback(() => {
    const psalmNumber = Number(psalmNumberInputRef.current?.value);
    if (isNaN(psalmNumber) || psalmNumber === 0) {
      setPsalmNumber(undefined);
    } else {
      setPsalmNumber(psalmNumber);
    }
  }, [psalmNumberInputRef, setPsalmNumber]);

  const onClickUpload = useCallback(() => {
    if (!songNameIsValid) {
      void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
      return;
    }
    const number = songName?.match(/^Psalm (?<number>\d+)/)?.groups?.number;
    if (number && !psalmNumber) {
      setPsalmNumber(Number(number));
    }
    fileChooserInputRef?.current?.click();
  }, [psalmNumber, setPsalmNumber, songName, songNameIsValid]);

  const onDelete = useCallback(() => {
    if (!currentOrg?.id) {
      return;
    }
    void (async () => {
      if (
        !(await confirm({
          confirmation: (
            <span>Press Confirm to delete {hymn?.title ? `'${hymn.title}'` : 'song'} from your hymnal</span>
          ),
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
        }))
      ) {
        return;
      }
      setShowSpinner(true);
      try {
        await setSongVisibility({ songSlug, isVisible: true });
        await server_api.deleteCustomSong(songSlug);
        await recordRecentlyDeletedSong(customMusicManifest, currentOrg.id, songSlug);
        proceed({});
      } catch (e: any) {
        setShowSpinner(false);
        await alert({ confirmation: <span>Error: {e.message}</span> });
      } finally {
        setShowSpinner(false);
      }
    })();
  }, [currentOrg, customMusicManifest, hymn, proceed, setSongVisibility, songSlug]);

  const onFileChosen = useCallback(() => {
    const file = fileChooserInputRef?.current?.files?.[0];
    if (!file || !currentOrg || !songName) {
      console.log('no file');
      return;
    }
    setFileToUpload(file);
  }, [currentOrg, songName]);

  const onClose = useCallback(() => {
    console.log(`finish`);
    proceed({});
  }, [proceed]);

  const onRetry = useCallback(() => {
    setStatus(UploadStatus.Before);
    setErrorMessage(undefined);
    setFileToUpload(undefined);
    setIsUploadSuccessful(false);
  }, []);

  const onFinish = useCallback(() => {
    if (!currentOrg || !songName || !songNameIsValid || !isValidPsalmNumber || !hymn) {
      if (!currentOrg) {
        console.error(`Missing org`);
        proceed({});
      }
      if (!hymn) {
        console.error(`Missing hymn`);
        proceed({});
      }
      if (!songName) {
        void alert({ confirmation: <span>Missing song name</span> });
      }
      if (!songNameIsValid) {
        void alert({ confirmation: <span>Song name may not contain a slash (/)</span> });
        return;
      }
      if (!isValidPsalmNumber) {
        void alert({ confirmation: <span>Invalid Psalm number</span> });
      }
      return;
    }
    const anythingChanged = songName !== hymn.title || psalmNumber !== hymnPsalmNumber;
    if (!anythingChanged) {
      proceed({});
      return;
    }
    void (async () => {
      setShowSpinner(true);
      try {
        await server_api.renameCustomSong({ songSlug, songName, psalmNumber });
        await recordRecentlyAddedSong({
          customMusicManifest,
          org: currentOrg,
          songSlug,
          songName,
          psalmNumber,
        });
        proceed({});
      } catch (e: any) {
        setShowSpinner(false);
        await alert({ confirmation: <span>Error: {e.message}</span> });
      } finally {
        setShowSpinner(false);
      }
    })();
  }, [
    currentOrg,
    songName,
    songNameIsValid,
    isValidPsalmNumber,
    hymn,
    psalmNumber,
    hymnPsalmNumber,
    proceed,
    songSlug,
    customMusicManifest,
  ]);

  const uploadFile = useCallback(async () => {
    if (busyUploading) {
      return;
    }
    busyUploading = true;
    try {
      if (
        status !== UploadStatus.Before ||
        !fileToUpload ||
        !currentOrg ||
        !songName ||
        isUploadSuccessful ||
        errorMessage
      ) {
        return;
      }
      setStatus(UploadStatus.Uploading);
      const releaseAppReloadHold = holdAppReload();
      try {
        const songNumber = songName && (await songNumberToOverwrite(customMusic[currentOrg.id], songName, psalmNumber));
        const newlyAssignedSongNumber = await server_api.uploadOrgCustomMusicFile({
          orgId: currentOrg.id,
          songName,
          psalmNumber,
          file: fileToUpload,
          songNumber,
        });
        console.log(newlyAssignedSongNumber);
        // setAssignedSongNumber(newlyAssignedSongNumber);
        setStatus(UploadStatus.Succeeded);
      } catch (error: any) {
        setErrorMessage(error.message);
        setStatus(UploadStatus.Failed);
      }
      releaseAppReloadHold();
      setFileToUpload(undefined);
      setIsUploadSuccessful(true);
    } finally {
      busyUploading = false;
    }
  }, [currentOrg, customMusic, errorMessage, fileToUpload, isUploadSuccessful, psalmNumber, songName, status]);

  useEffect(() => {
    void uploadFile();
  }, [uploadFile]);

  const isMobile = getPlatform() === Platform.Mobile;

  const onClickDialog = useCallback(
    (e: any) => {
      const didClickBackdrop = e.currentTarget === e.target;
      if (didClickBackdrop) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <>
      <Dialog key="dialog" $isMobile={isMobile} ref={dialogRef} onClick={onClickDialog}>
        {fileToUpload || isUploadSuccessful ? (
          <DialogContent>
            {status === UploadStatus.Before || status === UploadStatus.Uploading ? (
              <>
                <Message $widthInChars={16}>Uploading file</Message>
                <Spinner />
              </>
            ) : status === UploadStatus.Failed ? (
              <Message $widthInChars={23}>
                File upload failed.
                <br />
                <br />
                {errorMessage ?? ''}
              </Message>
            ) : (
              <Message $widthInChars={23}>
                File upload successful!
                <br />
                <br />
              </Message>
            )}
            {status === UploadStatus.Before || status === UploadStatus.Uploading ? (
              <Button onClick={onClose}>CANCEL</Button>
            ) : status === UploadStatus.Failed ? (
              <>
                <Button onClick={onRetry}>RETRY</Button>
                <Button onClick={onClose}>CANCEL</Button>
              </>
            ) : (
              <Button onClick={onFinish}>DONE</Button>
            )}
          </DialogContent>
        ) : (
          <DialogContent>
            <input
              type="file"
              id="imgupload"
              style={{ display: 'none' }}
              ref={fileChooserInputRef}
              onChange={onFileChosen}
            />
            <InputWrapper $isMobile={isMobile}>
              <CustomMusicInput
                key="song-name"
                type="text"
                style={{ width: '100%' }}
                onChange={onSongNameChanged}
                ref={songNameInputRef}
                defaultValue={songName}
                placeholder={'Song Name'}
                $songNameIsValid={songNameIsValid}
              />
              <PsalmWrapper>
                <Message $widthInChars={5} $noPadding={true}>
                  Psalm
                </Message>
                <TextInput
                  $width="5rem"
                  $isError={!isValidPsalmNumber}
                  title={isValidPsalmNumber ? undefined : 'Invalid song number'}
                  type="number"
                  onChange={onPsalmNumberChanged}
                  defaultValue={psalmNumber}
                  ref={psalmNumberInputRef}
                />
              </PsalmWrapper>
            </InputWrapper>
            <ButtonStack>
              <Button onClick={onFinish}>DONE</Button>
              <Button hidden={true} onClick={onClickUpload}>
                REPLACE PDF
              </Button>
              <Button onClick={onDelete} isDangerous={true}>
                DELETE
              </Button>
            </ButtonStack>
            <HiddenSongCheckBox isHiddenSong={isHiddenSong} setHiddenSong={setHiddenSongWrapper} />
          </DialogContent>
        )}
        {showSpinner && <ModalSpinner />}
      </Dialog>
    </>
  );
};

const InputWrapper = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  width: 100%;
  padding-bottom: 30px;
`;

const CustomMusicInput = styled(TextInput)<{ $songNameIsValid: boolean }>`
  ${(props) => (props.$songNameIsValid ? '' : 'border-color: var(--color-invalid-input)')};
`;

const Dialog = styled.dialog<{ $isMobile?: boolean }>`
  width: ${(props) => (props.$isMobile ? '95vw' : '80vw')};
  max-width: 800px;
  border: none;
  border-radius: 20px;
  position: fixed;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  margin: auto;
  background: var(--color-background);
  padding: 20px;
  top: 0;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 20px;
`;

async function songNumberToOverwrite(
  customMusic: Hymn[],
  songName: string,
  psalmNumber?: number,
): Promise<string | undefined> {
  if (!customMusic) {
    return undefined;
  }

  for (const hymn of customMusic) {
    const hymnPsalmNumber = hymn.psalm?.match(/(?<number>\d+)/)?.groups?.number;
    if (
      hymn.title.toLowerCase() === songName.toLowerCase() &&
      !!hymn.psalm === !!psalmNumber &&
      (!psalmNumber || psalmNumber === Number(hymnPsalmNumber))
    ) {
      if (
        await confirm({
          confirmation: <span>Would you like to overwrite the existing song with the same name?</span>,
        })
      ) {
        return hymn.songNumber;
      }
      break;
    }
  }
}

const editCustomSong = createConfirmation(confirmable(EditCustomSong));

export function EditSongButton({ songSlug }: { songSlug: SongSlug }) {
  const { customMusic, customMusicManifest } = useCustomMusic();
  const { hasPermission } = use(EditButtonContext) ?? {};
  const onClick = useCallback(
    async (event: any) => {
      event.stopPropagation();
      await editCustomSong({ customMusic, customMusicManifest, songSlug });
    },
    [customMusic, customMusicManifest, songSlug],
  );
  if (!hasPermission || !isCustomSong(songSlug)) {
    return;
  }
  return <Pencil style={{ width: '27px', height: '27px' }} onClick={onClick} />;
}

const PsalmWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 20ch;
  gap: 20px;
`;
